// The scope lives in navigation.ts, where all eight of this console's cross-boundary scopes are
// collected so "what can this console reach" is answerable from one file. Re-exported so a reader
// of this module finds it, and so channel-scopes.mjs sees one literal rather than two.
export { EVENT_REPLAYS_SCOPE } from "./navigation.ts";

import type { PlatformReplaySummary } from "@tdelivr/api-client/generated/event-platform/event-replay-control.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { asDay, type BoardRow } from "./board.ts";

/**
 * What the replay control board does to the summaries EVT-04 returns, away from any rendering.
 *
 * Built to `16 WP15 Platform Machinery.dc.html`, page `replay`, against EVT-04
 * `GET /v1/platform/event-replays` — published by
 * [ADR 0060](../../../docs/adr/0060-a-platform-scoped-replay-listing-is-a-separate-operation-and-a-separate-scope.md),
 * which is **EVT-04's first public operation of any kind**: before it the service published
 * `GET /v1/service-info` and five `/internal/v1` operations, each with a `tenantId` on the path and
 * each refused when the verified context named a different tenant. A board of every replay on the
 * platform may not be drawn from one of those (ADR 0048).
 *
 * ```
 * replay:['Replay control','EVT-04','Replays run with a concurrency limit and per-partition checkpoints. Sensitive streams need two approvers.',
 *   [['Running','1','active','48,210 events'],['Awaiting approval','1','human','1 of 2'],
 *    ['Blocked projections','1','caution','delivery projection'],['Completed today','3','done','']],
 *   ['Replay','Stream','Progress'],
 *   [['RPL-0311','payments.settled.v2','partitions 0–11 · 38%','active','running'],
 *    ['RPL-0312','delivery.events.v4','—','human','1 of 2 approvals'],
 *    ['RPL-0309','search.projection','100%','done','completed']],
 *   'info','Operations consoles show "replay in progress" while a projection is blocked.']
 * ```
 *
 * ## `1 of 2 approvals` is the published pair and not a rule this file restates
 *
 * The design's second row is badged `human · 1 of 2 approvals`. The numerator is `approvalCount`
 * and the denominator is `requiredApprovals`, and **both arrive as counts** — the identities of the
 * requester and the approvers never cross the tenancy boundary at all. ADR 0060's §Context is
 * mostly about that trade: a derived number is published in order to withhold a name.
 *
 * The denominator is read rather than computed, and this is the one line in this file where the
 * temptation is real. `requiredApprovals` is `sensitivity === "sensitive" ? 2 : 1` in
 * `services/event-platform/event-replay-control/src/domain.ts`, and `sensitivity` is published, so
 * this module could work it out. It must not: a client restating a server rule is a second
 * implementation of it, and the day the rule gains a third case — a risk class, a topic, a tenant
 * policy — this board would draw `1 of 1` on a job that cannot start, with no error anywhere.
 * ADR 0038 and ADR 0033 §2 both circle exactly that defect, and ADR 0060 §Context names this board
 * as the reason the count is on the wire. So `approvalsText` below reads two fields and does no
 * arithmetic.
 *
 * ## The `Progress` column, which is three designed grammars and one of them is not drawable
 *
 * | the design's cell            | what this board draws                      | why                       |
 * | ---------------------------- | ------------------------------------------ | ------------------------- |
 * | `partitions 0–11 · 38%`      | `4 of 12 partitions`                       | both halves are absent    |
 * | `—`                          | `—`, word for word                         | the job has not started   |
 * | `100%`                       | `100%`, word for word                      | `status = 'completed'`    |
 *
 * **`38%` is not computable and drawing it would mean inventing it.** A checkpoint is
 * `{partition, nextOffset, complete}` — no start offset, no end offset — and `scope.maximumEvents`
 * is a ceiling the plan set rather than a count of what has run, so a fraction of *events* replayed
 * has no source in anything this service stores, let alone publishes. What does exist is
 * `completedPartitions` over `partitionCount`, which ADR 0060 §4 names as "what the progress cell
 * can honestly be drawn from", and that is what `progressText` writes. It is written in the design's
 * own fraction grammar — `1 of 2` is this page's tile and `1 of 2 approvals` is this page's badge —
 * rather than as a second percentage, because a percentage of partitions under a design that means a
 * percentage of events is the same wrong number with better manners.
 *
 * **`partitions 0–11` cannot be drawn either, and it is the one place ADR 0060 publishes less than
 * the design's cell asks for.** Only `partitionCount` leaves the service; the partition numbers
 * themselves do not, because `checkpoints[].nextOffset` is a broker offset inside one tenant's topic
 * and the list travels with it. That bullet is the last one in ADR 0060 §4 and is recorded there
 * rather than widened quietly. The count is drawn in its place and the reason rides on the cell.
 *
 * Both absences are **Q-201**, which is proposed in `docs/proposals/design-evt.md` §EVT-04 and is
 * **not yet in** `docs/design/DESIGN_QUESTIONS.md` — cited under that number here because ADR 0060
 * already cites it under that number, so whoever records it records it where these two already
 * point. Either a checkpoint carries the offset it started from, or a replay carries a running count
 * of events emitted; both are fields, neither is route work.
 *
 * ## `—` on the second row is an answer, not an absence, and is drawn differently for that reason
 *
 * The design writes `—` in the `Progress` cell of its awaiting-approval row, and the data agrees
 * with it: a replay that has not started has run no partitions. So that cell is **not** marked
 * `absent` — it is ordinary cell text whose value is the design's own em dash, which keeps it in the
 * design's `--text2` rather than in `--text3`, and keeps it out of the vocabulary this console
 * reserves for a column no operation can fill. `partner-api.ts`'s `Webhook` column is the other
 * kind: API-03 publishes nothing, so that one is `absent` and tinted. The two look nearly the same
 * on screen and say opposite things, and the distinction is the whole reason both exist.
 *
 * ## `RPL-0311` is a UUID here
 *
 * `replay_id` is a UUID — `^[0-9a-f]{8}-…` in EVT-04's own cursor pattern — and `RPL-0311` is a
 * short readable code this platform does not mint anywhere. The column draws the identifier it is
 * given, in Geist Mono, which is both the design's own treatment of its `RPL` prefix and this
 * console's rule for a published identifier drawn where the design writes a name. **Q-196** records
 * the width that costs, for `ten_7Q2M` against a tenant UUID, and this is the same divergence one
 * page over.
 *
 * ## What has no source
 *
 * **All four stat tiles**, and they fail for four different reasons, which is why the row is not
 * drawn rather than drawn thin:
 *
 *   - `Running · 1 · 48,210 events` — the count is R-04's (no collection read on this platform
 *     publishes a total, so a tile reading `1` would mean "1 so far" under a label meaning "1"), and
 *     the caption is Q-201's: `scope.maximumEvents` is a plan ceiling and is withheld in any case.
 *   - `Awaiting approval · 1 · 1 of 2` — R-04 again for the count. The caption is the one part of
 *     this row that has a source, and it is the badge's.
 *   - `Blocked projections · 1 · delivery projection` — **SEA-02's**, not EVT-04's, and SEA-02
 *     publishes nothing a browser can call. ADR 0060 §Context declines to take another service's
 *     decision, as ADR 0052 §6 did for API-03. **Q-203**, also proposed and not yet recorded.
 *   - `Completed today · 3` — `status = 'completed'` carries no completion timestamp of its own and
 *     `updated_at` is the last transition of any kind, so "today" has nothing to compare.
 *
 * Q-178 carries the tile row across this console.
 *
 * **Withheld on purpose, and this board wants none of it back.** `scope.payloadReference` is the
 * address of a stored event set, and one page of these is a map to every tenant's replayable event
 * bodies; `reason` is up to five hundred characters a tenant's operator wrote about a live incident;
 * `requestedBy` and `approvals` are identities; `scope.fromTime`, `toTime`, `maximumEvents` and
 * `eventsPerSecond` are how hard one tenant is willing to push its own consumers. ADR 0060 §4 argues
 * each one. `12 Approval Pattern.dc.html` draws nearly all of them for a **tenant** audience, which
 * is a different read and a different decision — Q-202 — and no field crosses here for its sake.
 */

/**
 * The design's `Progress` cell, in each of the grammars the published counts can carry.
 *
 * `100%` is exact rather than derived: `status = 'completed'` is the service's own statement that
 * every partition finished, and ADR 0060 §Context says so in those words. It is not computed from
 * the counts, because a completed job whose final checkpoint has not been written back would then
 * draw `11 of 12 partitions` on a row the service calls finished.
 *
 * `—` is the design's own cell for a job that has not started, and is returned as text — see this
 * file's header on why it is not an `absent` cell.
 */
export function progressText(replay: PlatformReplaySummary): string {
  if (replay.status === "completed") return "100%";
  if (replay.status === "awaiting_approval" || replay.status === "approved") return "—";
  return `${String(replay.completedPartitions)} of ${String(replay.partitionCount)} partitions`;
}

/** The design's `1 of 2 approvals`: two published counts, and no arithmetic. See the header. */
export function approvalsText(replay: PlatformReplaySummary): string {
  return `${String(replay.approvalCount)} of ${String(replay.requiredApprovals)} approvals`;
}

/**
 * The badge's class and word, from `status` and the two approval counts.
 *
 * The design's three badges settle three of the six states EVT-04 publishes:
 *
 * | the design's row                                      | class    | word               |
 * | ----------------------------------------------------- | -------- | ------------------ |
 * | `RPL-0311 · payments.settled.v2 · partitions 0–11 · 38%` | `active` | `running`          |
 * | `RPL-0312 · delivery.events.v4 · —`                   | `human`  | `1 of 2 approvals` |
 * | `RPL-0309 · search.projection · 100%`                 | `done`   | `completed`        |
 *
 * The other three have no designed row and the words are the contract's own — `approved`, `failed`,
 * `cancelled` — which is `partner-api.ts`'s precedent for a published state the design did not draw.
 * `cancelled` is `closed` rather than `failed` because a cancelled replay is a decision and a failed
 * one is news.
 *
 * **`approved` is `idle` rather than `waiting`, and the reason is the detail panel's action.** Both
 * classes are the token system's neutral, and `idle`'s own label is "Not started" with `queued`
 * among its examples, which is exactly what an approved replay is: inside the per-tenant
 * concurrency limit's queue, with nothing having happened to it yet. `waiting` means another party
 * holds it, which was true before the approvals landed and is not true now. The visible difference
 * is in `RowDetail`'s `ACTION_BY_CLASS`, which is the design's own map: it offers `waiting` the
 * command `Resend`, and `Resend` is not a thing one does to a replay. `idle` is not in that map, so
 * no action is drawn — which is also the design's behaviour for a class it does not map, its `actS`
 * setting `display: none`. Nothing is compared either way, because the design draws no row for this
 * state.
 *
 * A state is never shown by colour alone: every one of these carries a glyph and a word, and the six
 * words are six different words.
 */
export function stateOf(replay: PlatformReplaySummary): {
  classKey: StateClassKey;
  label: string;
  title?: string;
} {
  switch (replay.status) {
    case "awaiting_approval":
      return {
        classKey: "human",
        // The design's own badge word for this row, with the published counts in it.
        label: approvalsText(replay),
        title:
          "How many approvals this job has and how many it needs, both published as counts. The approvers are not named: EVT-04 withholds every identity so a cross-tenant board can draw this badge without one crossing the boundary. ADR 0060 §Context.",
      };
    case "approved":
      return {
        classKey: "idle",
        label: "approved",
        title:
          "Approved and not started. EVT-04 refuses a third concurrent replay per tenant, so an approved job can wait behind two running ones. The design has no row for this state.",
      };
    case "running":
      return { classKey: "active", label: "running" };
    case "completed":
      return { classKey: "done", label: "completed" };
    case "failed":
      return {
        classKey: "failed",
        label: "failed",
        title:
          "Replays checkpoint per partition, so a restarted replay resumes where this one stopped. Why it stopped is not published: the plan's reason and its payload reference are withheld. ADR 0060 §4.",
      };
    case "cancelled":
      return { classKey: "closed", label: "cancelled" };
  }
}

/**
 * One row of the replay control board.
 *
 * The detail panel carries the five published fields the grid's three columns cannot: which tenant
 * the job belongs to, whether the stream is sensitive, whether it is a dry run, the partition counts
 * and the approval counts. Nothing else — the plan's window, its rate limits, its reason and its
 * requester are withheld by ADR 0060 §4, and the tenant-audience screen that draws them is Q-202's
 * rather than this one's. The glyph is the design's own form icon for this page, `replay`.
 */
export function replayRow(replay: PlatformReplaySummary): BoardRow {
  const state = stateOf(replay);
  return {
    /*
     * The tenant and the replay, which is EVT-04's primary key and this listing's cursor —
     * `ORDER BY tenant_id, replay_id`, both halves immutable. Prefixed with the tenant because the
     * listing spans tenants: a React key that collided would make one tenant's row replace
     * another's on a board whose whole purpose is seeing all of them.
     */
    id: `${replay.tenantId}:${replay.replayId}`,
    cells: [
      {
        text: replay.replayId,
        // The design's own grid sets Geist Mono for a value matching its prefix list, `RPL` among
        // them. A UUID matches none of them and is mono here for this console's own rule: a
        // published identifier drawn where the design writes a readable code. Q-196.
        mono: true,
        title:
          "EVT-04 identifies a replay by a UUID. The design's RPL-0311 is a short readable code this platform does not mint anywhere. Q-196.",
      },
      {
        // `payments.settled.v2`. Not mono: it matches none of the design's mono prefixes, so its own
        // grid draws a topic in Geist at 400 in `--text2`.
        text: replay.topic,
      },
      {
        text: progressText(replay),
        title:
          replay.status === "completed"
            ? "Exact: the service reports this job as completed, so every partition finished."
            : "Completed partitions over total partitions, which is what EVT-04 publishes. The design's 38% is a share of events replayed and nothing stores one — a checkpoint carries no start or end offset, and scope.maximumEvents is a plan ceiling. Its partitions 0–11 needs the partition list, and only the count leaves the service. Q-201, ADR 0060 §4.",
      },
    ],
    state,
    // The design tints a row for its `human` and `failed` classes only.
    ...(state.classKey === "human" || state.classKey === "failed" ? { tinted: true } : {}),
    detail: [
      { label: "Tenant", value: replay.tenantId, mono: true },
      { label: "Replay", value: replay.replayId, mono: true },
      { label: "Stream", value: replay.topic },
      {
        label: "Partitions",
        value: `${String(replay.completedPartitions)} of ${String(replay.partitionCount)} complete`,
      },
      { label: "Approvals", value: approvalsText(replay) },
      /*
       * The contract's own two words, `sensitive` and `standard`, rather than a sentence about what
       * they mean. `sensitive` is why `requiredApprovals` reads 2 on a row, and the panel shows both
       * so an operator can see the pair without this console asserting the rule between them.
       */
      { label: "Sensitivity", value: replay.sensitivity },
      {
        label: "Dry run",
        // A dry run plans and checkpoints without publishing anything a consumer will see, which is
        // worth saying beside the word: `Yes` alone reads as a smaller claim than it is.
        value: replay.dryRun ? "Yes — nothing reaches a consumer" : "No",
      },
    ],
    /*
     * Two of the design's three `History` lines, and the third is Q-107's.
     *
     * That block reads `Created`, `Last changed` and `Version` with a person's name beside the first
     * two on every page of every one of these design files. EVT-04 records `requestedBy` and
     * `approvals` and ADR 0060 §4 withholds both as identities, so these are the instants without
     * the names. `Last changed` is `updatedAt`, which is the last transition of any kind — the same
     * field whose want of a meaning is why `Completed today` has no source.
     */
    history: [
      { label: "Created", value: asDay(replay.createdAt) },
      { label: "Last changed", value: asDay(replay.updatedAt) },
      { label: "Version", value: `v${String(replay.version)}` },
    ],
    // The design takes the detail panel's glyph from the page's own form icon, `FORMS.replay[2]`.
    glyph: "replay",
  };
}

/**
 * The states this listing can answer, in the order `stateOf` decides them.
 *
 * `awaiting approval` rather than the badge's own `1 of 2 approvals`: the badge word carries this
 * row's counts, so using it as a filter value would offer one option per distinct fraction — `1 of
 * 2 approvals` and `0 of 1 approvals` as two unrelated states. The filter groups by the status EVT-04
 * publishes, which is what an operator is choosing between.
 */
export const REPLAY_STATES: readonly string[] = [
  "awaiting approval",
  "approved",
  "running",
  "failed",
  "cancelled",
  "completed",
];

/** Which filter option a row answers to, which is its status in the filter's own words. */
export function filterLabel(replay: PlatformReplaySummary): string {
  return replay.status === "awaiting_approval" ? "awaiting approval" : replay.status;
}

/** The states present on the rows the board is holding, so the filter offers nothing empty. */
export function statesPresent(replays: readonly PlatformReplaySummary[]): readonly string[] {
  const present = new Set(replays.map(filterLabel));
  return REPLAY_STATES.filter((label) => present.has(label));
}

/**
 * What the board owes an operator about the `Progress` column and about its own span.
 *
 * Two sentences because there are two things a complete-looking board of this page would imply and
 * cannot support. The span is `platform-audit.ts`'s `tenantSpanNote` and `partner-api.ts`'s, in the
 * same words for the same reason: nothing publishes how many tenants exist — `POST /v1/tenants`
 * exists and there is no `GET` — so "every replay across 5 of 7 tenants" is unsayable and "replays
 * from 3 tenants" is exactly true. The column's sentence is said rather than implied because
 * `4 of 12 partitions` sits where the design drew a percentage of events, and an operator reading a
 * progress column is entitled to know which thing is being counted.
 */
export function replaySpanNote(
  replays: readonly PlatformReplaySummary[] | undefined,
): string | undefined {
  if (replays === undefined) return undefined;
  const tenants = new Set(replays.map((replay) => replay.tenantId)).size;
  if (tenants === 0) return undefined;
  return `Replays from ${String(tenants)} tenant${tenants === 1 ? "" : "s"}. Progress counts completed partitions out of total partitions: nothing publishes a count of events replayed, and only the number of partitions leaves the service rather than the partition numbers.`;
}

/**
 * The scope this board spends.
 *
 * EVT-04's first derived scope, stated in the handler that enforces it (ADR 0054) and named here as
 * a plain string literal so `tooling/scripts/lib/channel-scopes.mjs` can read it off this
 * application's own AST and derive the Keycloak grant from it — ADR 0049 §3, and the notification
 * bell's three-console 403 is what happens when a console spends a scope it never names.
 *
 * **Declared here rather than beside the other four in `navigation.ts`**, which is where this
 * console's scope constants live, only because that file is wired centrally and this board was built
 * against it rather than into it. The derivation does not care — it walks every file under
 * `apps/platform-console/src` — but a reader looking for the console's scopes will look there, so
 * this constant wants moving when the page is routed.
 */

/**
 * Why the designed `Request replay` form is drawn and refused.
 *
 * Its sections are `Stream` — `Stream`, `From`, `To` — and `Run` — `Concurrency limit`,
 * `Target consumer`, `Reason` — and its primary is `Submit for approval`. Four reasons, and any one
 * is enough:
 *
 *   - **The plan command is `/internal/v1` only.** `POST /internal/v1/tenants/{tenantId}/event-replays`
 *     is not exposed by the gateway (ADR 0029), and ADR 0060 §5 publishes a read and nothing else:
 *     a cross-tenant write would have to decide whose tenant it acted for.
 *   - **It takes a `tenantId` on the path and this form has no field for one.** Every other form in
 *     this design file that spans tenants offers a `Tenant` select; this one does not.
 *   - **It requires `scope.payloadReference`, `scope.partitions`, `scope.maximumEvents` and
 *     `scope.eventsPerSecond`**, all four `.strict()` and required, and the form has a field for
 *     none of them. Two of its own fields are not fields on the wire at all: `Concurrency limit` is
 *     a server rule — EVT-04 refuses a third running replay per tenant — and `Target consumer` is
 *     not part of a replay plan.
 *   - **The approval the button submits for needs two different people.** `approveReplay` refuses
 *     the requester and refuses a previous approver, and this listing publishes neither identity, so
 *     one administrator could not satisfy it and this console could not tell whether they had.
 *
 * Drawn and disabled rather than removed, because `CLAUDE.md` section 1 says the add or edit form is
 * not to be removed, and rather than wired to something that would answer 404.
 */
export const CREATE_REFUSAL =
  "EVT-04 publishes the replay plan on /internal/v1 only, which the gateway does not expose, and it requires a payload reference, a partition list and two rate bounds — this form has a field for none of them, and its Concurrency limit and Target consumer are not fields on the wire. Approval needs two different people and no identity is published. ADR 0060 §5, Q-202.";
